import { CruiseCabinNo } from "../../cruises/cruise-cabin.interface"
import { CabinGradeType } from "../../cruises/cruise-ship-cabin-grade.interface"
import { CruiseSailingId } from "../../cruises/cruise-sailing.interface"
import { TripRoomID } from "../../trips/trip-room.interface"
import { UserID } from "../../user/user.interface"
import { PaymentTransaction } from "./payment-request-transaction.types"
import { CruiseShipDeckID } from "../../cruises/cruise-ship-deck.interface"
import { CruiseShipId } from "../../cruises/cruise-ship.interface"
import { PaymentPlan } from "./payment-request.types"

export type PaymentLineItemID = string

export enum PaymentLineItemTypeEnum {
  CabinBooking = "cabinBooking",
  Other = "other",
}

export type PaymentLineItemType = `${PaymentLineItemTypeEnum}` | PaymentLineItemTypeEnum

export type PaymentLineItemMeta = Record<string, string>

export type PaymentLineItem<TMeta = PaymentLineItemMeta> = {
  id: PaymentLineItemID

  type: PaymentLineItemType

  paymentPlan?: PaymentPlan

  tripRoomId: TripRoomID

  /**
   * Represents the currency code in ISO 4217 format.
   * This variable holds a three-letter code used to define the name of the currency.
   * (e.g., USD)
   */
  currency: string

  /**
   * Represents the amount without taxes and fees as a string.
   */
  amount: string

  /**
   * Represents the taxes and fees as a string.
   */
  taxesAndFees?: string

  /**
   * Represents the total amount as a string.
   */
  total: string
  totalDueDate?: Date
  totalPaymentDate?: Date

  /**
   * Represents the deposit amount as a string.
   */
  deposit?: string
  depositPaymentDate?: Date

  /**
   * Represents an optional array of payment transactions associated with a specific operation or process.
   */
  transactions: PaymentTransaction[]

  label?: string
  description?: string

  created: Date
  createdBy: UserID

  updated: Date
  updatedBy: UserID

  meta: TMeta
}

export type CabinPaymentLineItemMeta = {
  cruiseSailingId: CruiseSailingId
  cruiseShipId: CruiseShipId

  deckId?: CruiseShipDeckID
  deckName?: string

  cabinNo?: CruiseCabinNo
  cabinGradeName?: string
  cabinGradeType?: CabinGradeType
}

export type CabinPaymentLineItem = PaymentLineItem<CabinPaymentLineItemMeta>

/**
 * @param {Pick<PaymentLineItem, "type">} paymentLineItem
 * @return {boolean}
 */
export function isCabinPaymentLineItem(
  paymentLineItem: Pick<PaymentLineItem, "type">,
): paymentLineItem is CabinPaymentLineItem {
  return paymentLineItem.type === PaymentLineItemTypeEnum.CabinBooking
}

export type PaymentLineItemPayload<TMeta> = Pick<
  PaymentLineItem,
  | "type"
  | "tripRoomId"
  | "currency"
  | "amount"
  | "taxesAndFees"
  | "total"
  | "totalDueDate"
  | "totalPaymentDate"
  | "deposit"
  | "depositPaymentDate"
  | "label"
  | "description"
> & {
  meta: TMeta
}
